import React, { useMemo, useState } from 'react';
import { useOrgMembers, useOrgInvites, useActiveOrgId } from '@/hooks/queries';
import { useRorosActors } from '@/hooks/roros/actors';
import { Avatar } from '@/components/ui/Avatar/Avatar';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { SimpleTooltip } from '@/components/ui/Tooltip/Tooltip';
import { Search, Mail, UserCheck } from 'lucide-react';
import { RorosActorType } from '@/types/roros/enums';
import { t } from '@/lib/i18n';

/**
 * UserPicker — searchable list of the organization's members for actor creation.
 *
 * Behaviour:
 * - Active members with no existing actor → selectable
 * - Active members who already have an actor → shown disabled with "Already an actor"
 * - Pending invites → shown disabled with tooltip "Available once user accepts invite"
 * - Suspended/removed members → hidden entirely
 *
 * Scoped to the currently-active organization. Read-only; no mutations.
 */

interface UserPickerProps {
  selectedUserId: string | null;
  onSelect: (userId: string) => void;
}

interface MemberRow {
  user_id: string;
  email: string;
  // Backend (orgs.repository.listMembers) returns snake_case.  Keeping
  // both shapes typed avoids a silent miss if a consumer reads the wrong one.
  full_name?: string | null;
  avatar_url?: string | null;
  fullName?: string;
  role: 'owner' | 'admin' | 'member';
  status?: 'active' | 'invited' | 'suspended';
}

interface InviteRow {
  id: string;
  email: string;
  status?: 'pending' | 'accepted' | 'expired';
  accepted_at?: string | null;
  expires_at?: string;
}

type PickerRow =
  | { kind: 'member'; userId: string; email: string; fullName?: string; avatarUrl?: string | null; role: string; disabled: boolean; disabledReason?: string }
  | { kind: 'pending'; inviteId: string; email: string };

export function UserPicker({ selectedUserId, onSelect }: UserPickerProps) {
  const orgId = useActiveOrgId() ?? undefined;
  const membersQuery = useOrgMembers(orgId);
  const invitesQuery = useOrgInvites(orgId);
  const actorsQuery = useRorosActors();

  const [search, setSearch] = useState('');

  /** Map user_id → true when they already have a human actor in this org. */
  const takenUserIds = useMemo(() => {
    const set = new Set<string>();
    if (actorsQuery.data) {
      for (const a of actorsQuery.data) {
        if (a.actorType === RorosActorType.HUMAN && a.userId) {
          set.add(a.userId);
        }
      }
    }
    return set;
  }, [actorsQuery.data]);

  const rows = useMemo<PickerRow[]>(() => {
    const out: PickerRow[] = [];

    // Active members (exclude suspended — per product decision)
    const members = (membersQuery.data ?? []) as MemberRow[];
    for (const m of members) {
      // listMembers already filters out inactive users, but belt-and-braces:
      if (m.status === 'suspended') continue;
      const already = takenUserIds.has(m.user_id);
      out.push({
        kind: 'member',
        userId: m.user_id,
        email: m.email,
        // Accept either camelCase (legacy) or snake_case (current backend).
        fullName: m.fullName ?? m.full_name ?? undefined,
        avatarUrl: m.avatar_url ?? null,
        role: m.role,
        disabled: already,
        disabledReason: already ? 'Already an actor' : undefined,
      });
    }

    // Pending invites (not yet accepted)
    const invites = (invitesQuery.data ?? []) as InviteRow[];
    for (const inv of invites) {
      // Treat anything that hasn't been accepted as pending. The invites API
      // exposes `status` for some callers and `accepted_at` for the raw row;
      // accept either.
      const isPending = inv.status === 'pending' || (inv.status == null && !inv.accepted_at);
      if (!isPending) continue;
      out.push({ kind: 'pending', inviteId: inv.id, email: inv.email });
    }

    // Client-side filter by search
    const q = search.trim().toLowerCase();
    if (!q) return out;
    return out.filter((r) =>
      r.kind === 'member'
        ? r.email.toLowerCase().includes(q) || (r.fullName ?? '').toLowerCase().includes(q)
        : r.email.toLowerCase().includes(q),
    );
  }, [membersQuery.data, invitesQuery.data, takenUserIds, search]);

  const isLoading = membersQuery.isLoading || invitesQuery.isLoading || actorsQuery.isLoading;

  return (
    <div>
      <div className="relative mb-3">
        <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('Search by name or email...')}
          className="pl-9"
        />
      </div>

      <div className="border rounded-lg max-h-64 overflow-y-auto divide-y">
        {isLoading && (
          <div className="p-4 text-sm text-muted-foreground text-center">{t('Loading users...')}</div>
        )}

        {!isLoading && rows.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground text-center">
            {search ? t('No matching users') : t('No eligible users in this organization')}
          </div>
        )}

        {!isLoading &&
          rows.map((row) => {
            if (row.kind === 'pending') {
              return (
                <SimpleTooltip
                  key={`inv:${row.inviteId}`}
                  content={t('Available once user accepts the invite')}
                  side="right"
                >
                  <div className="flex items-center gap-3 p-3 opacity-60 cursor-not-allowed">
                    <Avatar size="sm" name={row.email} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                        {row.email}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t('Invitation sent — awaiting acceptance')}
                      </div>
                    </div>
                    <Badge variant="secondary">{t('Pending')}</Badge>
                  </div>
                </SimpleTooltip>
              );
            }

            const isSelected = selectedUserId === row.userId;
            const content = (
              <Button
                type="button"
                variant="ghost"
                onClick={() => !row.disabled && onSelect(row.userId)}
                disabled={row.disabled}
                className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                  row.disabled
                    ? 'opacity-60 cursor-not-allowed'
                    : isSelected
                      ? 'bg-primary-50'
                      : 'hover:bg-muted'
                }`}
              >
                <Avatar
                  size="sm"
                  src={row.avatarUrl ?? undefined}
                  name={row.fullName || row.email}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {row.fullName || row.email}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{row.email}</div>
                </div>
                {isSelected && !row.disabled && (
                  <UserCheck className="h-4 w-4 text-primary-600" />
                )}
                {row.disabled ? (
                  <Badge variant="secondary">{row.disabledReason}</Badge>
                ) : (
                  <Badge variant="outline" className="capitalize">
                    {row.role}
                  </Badge>
                )}
              </Button>
            );

            return row.disabled && row.disabledReason ? (
              <SimpleTooltip
                key={`u:${row.userId}`}
                content={row.disabledReason}
                side="right"
              >
                {content}
              </SimpleTooltip>
            ) : (
              <div key={`u:${row.userId}`}>{content}</div>
            );
          })}
      </div>
    </div>
  );
}
